import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ShoppingCart, X, Clock, CheckCircle, AlertCircle, Edit2, Package, Send, Download, FileText, TrendingDown, Printer, AlertTriangle, BarChart3, UserCheck, Undo2, ClipboardCheck, MessageCircle, FileDown } from 'lucide-react';
import { medicineOrderAPI, vendorAPI, medicineAPI, plannerAPI, brandingAPI } from '../services/api';
import { useToast, usePrompt } from './UIProvider';
import { formatClinicDate, clinicToday } from '../utils/clinicTime';
import { scrollContentToTop } from '../utils/scroll';
import { RECEIVE_ORDER_HANDOFF } from './StockReceiptsManagement';
import ReorderSettingsView from './orders/ReorderSettingsView';
import OrderBoardView, { openOrderReview } from './orders/OrderBoardView';
import { printOrderSheet } from './orders/orderSheet';
import { shareOrderOnWhatsApp } from './orders/orderShare';
import { saveOrderPdf } from './orders/orderPdf';
import usePdfDownload from '../hooks/usePdfDownload';
import { FEATURES } from '../config/features';

const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  IN_REVIEW: { label: 'In review', color: 'bg-violet-100 text-violet-800', icon: UserCheck },
  REVIEWED: { label: 'Back from review', color: 'bg-amber-100 text-amber-800', icon: Undo2 },
  SUBMITTED: { label: 'Sent', color: 'bg-blue-100 text-blue-800', icon: Send },
  CONFIRMED: { label: 'Confirmed', color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
  SHIPPED: { label: 'Shipped', color: 'bg-purple-100 text-purple-800', icon: Package },
  PARTIALLY_RECEIVED: { label: 'Partial', color: 'bg-amber-100 text-amber-800', icon: Download },
  RECEIVED: { label: 'Received', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: X }
};

const AWAITING = ['SUBMITTED', 'CONFIRMED', 'SHIPPED', 'PARTIALLY_RECEIVED'];

/** "YYYY-MM-DD" plus n days, without passing a date-only string through the browser's time zone. */
const addDays = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
};

/** Sent, not yet in full, and more than three days past when it should have arrived. */
const isOverdue = (order) =>
  AWAITING.includes(order.status) && !order.closedShortAt && order.expectedDeliveryDate
  && addDays(order.expectedDeliveryDate, 3) < clinicToday();

const MedicineOrdersManagement = ({ hasPermission }) => {
  const showToast = useToast();
  const prompt = usePrompt();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('PHARMACY_ORDERS', action) : true),
    [hasPermission],
  );
  const [orders, setOrders] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('board'); // 'board', 'orders', 'settings'
  const [branding, setBranding] = useState(null);
  // The planner's board, loaded here because the cards above the tabs count from it too.
  const [board, setBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(true);
  const [boardError, setBoardError] = useState(null);
  // How long every order on the board is to last after its delivery; null leaves it to the planner.
  const [planDays, setPlanDays] = useState(null);
  const boardRequest = useRef(0);
  const [measures, setMeasures] = useState(null);

  // Switching the primary view swaps the whole page content — reset scroll so the new
  // view doesn't inherit the previous one's scroll position.
  useEffect(() => { scrollContentToTop(); }, [view]);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [ordersData, vendorsData, medsData, draftsData] = await Promise.all([
        medicineOrderAPI.getAll(),
        vendorAPI.getAll(),
        medicineAPI.getAll(),
        plannerAPI.getDrafts(),
      ]);
      setOrders(ordersData || []);
      setVendors(vendorsData || []);
      setMedicines(medsData || []);
      setDrafts((draftsData || []).filter(o => o.vendor));
    } catch { setError('Failed to load data'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const loadBoard = useCallback(async () => {
    // Only the latest answer is shown, when the period is changed again before one arrives.
    const mine = ++boardRequest.current;
    setBoardLoading(true);
    setBoardError(null);
    try {
      const data = await plannerAPI.getBoard(planDays);
      if (mine === boardRequest.current) setBoard(data);
    } catch (e) {
      if (mine === boardRequest.current) setBoardError(e.message || 'Could not work out what to order');
    } finally {
      if (mine === boardRequest.current) setBoardLoading(false);
    }
  }, [planDays]);
  useEffect(() => { loadBoard(); }, [loadBoard]);
  useEffect(() => {
    if (view !== 'orders') return;
    plannerAPI.getMeasures().then(setMeasures).catch(() => setMeasures(null));
  }, [view]);
  useEffect(() => { brandingAPI.get().then(setBranding).catch(() => {}); }, []);

  const refreshOrders = useCallback(async () => {
    try {
      const [ordersData, draftsData] = await Promise.all([medicineOrderAPI.getAll(), plannerAPI.getDrafts()]);
      setOrders(ordersData || []);
      setDrafts((draftsData || []).filter(o => o.vendor));
    } catch { /* the next full load will catch up */ }
    // The menu's count of urgent items follows what happens here.
    window.dispatchEvent(new Event('clinic:orders-changed'));
  }, []);

  const handleCancelOrder = async (orderId) => {
    const reason = await prompt({
      title: 'Cancel Order',
      subtitle: 'Provide a reason for cancellation',
      placeholder: 'e.g., Vendor unresponsive, no longer needed...',
      multiline: true,
      required: true,
      errorMessage: 'Please provide a reason',
      confirmText: 'Cancel Order',
      cancelText: 'Back',
      tone: 'danger',
    });
    if (reason === null) return;
    try {
      await medicineOrderAPI.cancel(orderId, reason);
      refreshOrders();
      showToast('success', 'Order cancelled');
    } catch (err) { showToast('error', 'Failed to cancel order: ' + err.message); }
  };

  // A delivery is received as a stock receipt, with its bill, batches and expiry dates, and set
  // against the order there. The old in-page Receive added counts straight onto the stock counter,
  // which the next dispense resynced away. This opens a new receipt with the order already chosen.
  const handleReceiveOrder = (order) => {
    if (!order.vendor?.id) {
      showToast('warning', 'Set the supplier on this order first: a delivery is received from a supplier.');
      return;
    }
    try {
      sessionStorage.setItem(RECEIVE_ORDER_HANDOFF, JSON.stringify({ orderId: order.id, vendorId: order.vendor.id }));
    } catch { /* without storage the receipt page still opens; the order is picked there */ }
    window.location.hash = '/stock-receipts';
  };

  // The supplier is not sending the rest: what arrived stays received and the rest is let go.
  const handleCloseShort = async (order) => {
    const reason = await prompt({
      title: `Close ${order.orderNumber} short?`,
      subtitle: 'Why is the rest not coming?',
      message: 'What has arrived stays received. The lines still due are no longer expected, and nothing more can be received against this order.',
      placeholder: 'e.g. Covam is short at the depot',
      multiline: true,
      required: true,
      errorMessage: 'Say why the rest is not coming',
      confirmText: 'Close short',
      cancelText: 'Back',
    });
    if (reason == null) return;
    try {
      await medicineOrderAPI.closeShort(order.id, reason);
      refreshOrders();
      showToast('success', `${order.orderNumber} closed short`);
    } catch (err) { showToast('error', 'Could not close the order: ' + err.message); }
  };

  const handlePrint = (order) => {
    if (!printOrderSheet(order, branding)) showToast('warning', 'The browser blocked the print window. Allow pop-ups for this site and try again.');
  };

  const handleShare = (order) => shareOrderOnWhatsApp(order, branding, showToast);

  const [downloadingPdf, downloadPdf] = usePdfDownload();
  const handleDownload = (order) => downloadPdf(() => saveOrderPdf(order, branding));

  const formatCurrency = (amt) => `Rs. ${Math.round(Number(amt) || 0).toLocaleString()}`;

  // One pass over the orders for the cards, so each card's number is what its tab shows.
  const awaiting = orders.filter(o => AWAITING.includes(o.status) && !o.closedShortAt);
  const overdue = awaiting.filter(isOverdue);
  const monthStart = clinicToday().slice(0, 8) + '01';
  const receivedThisMonth = orders.filter(o => o.status === 'RECEIVED' && o.receivedDate && o.receivedDate >= monthStart);

  const urgentCount = board?.tierCounts?.ORDER_TODAY || 0;
  const TABS = [
    { id: 'board', label: 'To order', icon: TrendingDown },
    { id: 'orders', label: 'Orders', icon: Package },
    { id: 'settings', label: 'Item Settings', icon: Edit2 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center"><ShoppingCart className="h-7 w-7 text-white" /></div>
            <div><h1 className="text-3xl font-bold text-gray-900">Medicine Orders</h1><p className="text-gray-600">What to order, from whom, and when</p></div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[{ icon: AlertTriangle, label: 'Run out before delivery', value: urgentCount, gradient: 'from-red-500 to-rose-600', onClick: () => setView('board') },
            { icon: FileText, label: 'Drafts saved', value: drafts.length, gradient: 'from-gray-500 to-slate-600', onClick: () => setView('board') },
            { icon: Clock, label: 'Awaiting delivery', value: awaiting.length, gradient: 'from-amber-500 to-orange-600', onClick: () => setView('orders') },
            overdue.length
              ? { icon: AlertTriangle, label: 'Overdue deliveries', value: overdue.length, gradient: 'from-red-500 to-rose-600', onClick: () => setView('orders') }
              : { icon: CheckCircle, label: 'Received this month', value: receivedThisMonth.length, gradient: 'from-emerald-600 to-emerald-700', onClick: () => setView('orders') }
          ].map((s, i) => (
            <button key={i} onClick={s.onClick} className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg text-left transition-shadow">
              <div className="flex items-center justify-between">
                <div><p className="text-gray-500 text-sm">{s.label}</p><p className="text-2xl font-bold text-gray-900">{s.value || 0}</p></div>
                <div className={`w-12 h-12 bg-gradient-to-br ${s.gradient} rounded-xl flex items-center justify-center`}><s.icon className="h-6 w-6 text-white" /></div>
              </div>
            </button>
          ))}
        </div>

        {/* View Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setView(tab.id)} className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg font-medium transition-all ${view === tab.id ? 'bg-emerald-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm'}`}>
              <tab.icon className="h-4 w-4" /><span>{tab.label}</span>
            </button>
          ))}
        </div>

        {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center"><AlertCircle className="h-5 w-5 text-red-500 mr-2" /><span className="text-red-700">{error}</span></div>}

        {/* Content */}
        {loading ? <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
        : view === 'board' ? <OrderBoardView board={board} loading={boardLoading} error={boardError} onReload={loadBoard} planDays={planDays} onPlanDaysChange={setPlanDays} vendors={vendors} medicines={medicines} can={can} branding={branding} onChanged={refreshOrders} />
        : view === 'settings' ? <ReorderSettingsView vendors={vendors} canEdit={can('EDIT')} />
        : (
          <div className="space-y-4">
            <MeasuresTile measures={measures} />
            <OrdersListView orders={orders} reviews={board?.reviews || []} onCancel={handleCancelOrder} onReceive={handleReceiveOrder} onCloseShort={handleCloseShort} onPrint={handlePrint} onShare={handleShare} onDownload={handleDownload} downloading={downloadingPdf} formatCurrency={formatCurrency} can={can} />
          </div>
        )}
      </div>
    </div>
  );
};

// Whether ordering is getting better, month by month. If these four are not falling, the settings
// are wrong, and the numbers say which way.
const MeasuresTile = ({ measures }) => {
  if (!measures || measures.length === 0) return null;
  const [now, before] = measures;
  const monthName = (m) => formatClinicDate(m.month, { month: 'long' });
  const money = (v) => `Rs ${Math.round(Number(v) || 0).toLocaleString()}`;
  const handPct = (m) => (m.plannedLines ? `${m.linesChangedByHand} of ${m.plannedLines}` : 'none yet');
  const cells = [
    ['Days items were out of stock', now.stockOutDays ?? 'not known yet', before ? (before.stockOutDays ?? 'not known') : null, 'Lower is better'],
    ['Bought without an order', `${now.boughtWithoutOrder} (${money(now.boughtWithoutOrderValue)})`, before ? before.boughtWithoutOrder : null, 'Emergency or local purchases'],
    ['Order lines changed by hand', handPct(now), before ? handPct(before) : null, 'Falls as the planner learns'],
    ['Expired on the shelf', money(now.expiredValue), before ? money(before.expiredValue) : null, 'Stock value lost to expiry'],
  ];
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
      <p className="px-4 pt-4 font-semibold text-gray-800 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-navy-700" /> How ordering is going in {monthName(now)}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
        {cells.map(([label, value, prev, hint]) => (
          <div key={label} className="p-4">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-xl font-bold text-gray-900">{value}</p>
            {prev != null && <p className="text-xs text-gray-500">{monthName(before)}: {prev}</p>}
            <p className="text-xs text-gray-400 mt-0.5">{hint}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const OrdersListView = ({ orders, reviews = [], onCancel, onReceive, onCloseShort, onPrint, onShare, onDownload, downloading = false, formatCurrency, can }) => {
  const allowed = (action) => (typeof can === 'function' ? can(action) : true);
  // Who has an order out for review, from the board, which reads it with the order's latest round.
  const reviewOf = new Map(reviews.map(r => [r.orderId, r]));
  const nonDraftOrders = orders.filter(o => o.status !== 'DRAFT');

  if (nonDraftOrders.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
        <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Orders Yet</h3>
        <p className="text-gray-500">Orders appear here once they are marked as sent</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {nonDraftOrders.map(order => {
        const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.DRAFT;
        const StatusIcon = sc.icon;
        const late = isOverdue(order);
        const review = reviewOf.get(order.id);
        const inReview = order.status === 'IN_REVIEW' || order.status === 'REVIEWED';
        return (
          <div key={order.id} className="bg-white rounded-2xl shadow-xl p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${sc.color}`}><StatusIcon className="h-6 w-6" /></div>
                <div>
                  <h3 className="font-bold text-gray-800">{order.orderNumber}</h3>
                  <p className="text-sm text-gray-500">
                    {order.vendor?.vendorName ? `${order.vendor.vendorName} • ` : ''}{formatClinicDate(order.orderDate, {}, 'Not sent')} • {order.items?.length || 0} items
                    {['PARTIALLY_RECEIVED', 'RECEIVED'].includes(order.status) && order.items?.length > 0 && (
                      <> • {order.items.filter(i => i.status === 'RECEIVED').length} of {order.items.filter(i => i.status !== 'CANCELLED').length} in full</>
                    )}
                    {order.closedShortAt && <> • closed short</>}
                    {order.submittedBy && <> • sent by {order.submittedBy}</>}
                    {inReview && review && (
                      <> • {order.status === 'IN_REVIEW' ? `with ${review.reviewerName} to review` : `back from ${review.reviewerName}, with ${review.raisedByName} to accept`}</>
                    )}
                  </p>
                  {late && (
                    <p className="text-sm text-red-700 font-medium mt-0.5">Overdue: expected {formatClinicDate(order.expectedDeliveryDate)}. Chase the supplier.</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="font-bold">{formatCurrency(order.totalAmount)}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${sc.color}`}>{sc.label}</span>
                {inReview ? (
                  review && (review.raisedByMe || review.reviewerIsMe) && (
                    <button onClick={() => openOrderReview(order.id)} className="px-4 py-2 bg-violet-50 hover:bg-violet-100 text-violet-800 rounded-lg font-medium flex items-center"><ClipboardCheck className="h-4 w-4 mr-1" />Open review</button>
                  )
                ) : (
                  <>
                    <button onClick={() => onPrint(order)} aria-label={`Print ${order.orderNumber}`} title="Order sheet" className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"><Printer className="h-4 w-4" /></button>
                    <button onClick={() => onDownload(order)} disabled={downloading} aria-label={`Download ${order.orderNumber} as a PDF`} title="Download the order sheet as a PDF" className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50"><FileDown className="h-4 w-4" /></button>
                    {/* Only an order out with the supplier is theirs to receive on WhatsApp. */}
                    {FEATURES.whatsappShare && AWAITING.includes(order.status) && !order.closedShortAt && (
                      <button onClick={() => onShare(order)} aria-label={`Share ${order.orderNumber} on WhatsApp`} title="Share on WhatsApp" className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-50"><MessageCircle className="h-4 w-4" /></button>
                    )}
                  </>
                )}
                {AWAITING.includes(order.status) && !order.closedShortAt && allowed('RECEIVE_ORDER') && (
                  <button onClick={() => onReceive(order)} title="Opens a new stock receipt against this order" className="px-4 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg font-medium flex items-center"><Download className="h-4 w-4 mr-1" />Receive</button>
                )}
                {order.status === 'PARTIALLY_RECEIVED' && !order.closedShortAt && allowed('RECEIVE_ORDER') && (
                  <button onClick={() => onCloseShort(order)} className="px-4 py-2 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg font-medium">Close short</button>
                )}
                {['SUBMITTED', 'CONFIRMED'].includes(order.status) && allowed('CANCEL_ORDER') && (
                  <button onClick={() => onCancel(order.id)} className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium">Cancel</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MedicineOrdersManagement;
